import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useWallet } from '@/hooks/useWallet.ts'
import {
  useTermsConsent,
  type TermsConsentSource,
} from '@/hooks/useTermsConsent.ts'
import { TermsCheckbox } from '@/components/ui/TermsCheckbox/index.ts'
import { cn } from '@/utils/cn.ts'

/** Fallback copy if locale bundles lag behind deploys (t() prefers JSON when present). */
const HERO_BETA_FALLBACK: Record<
  'en' | 'it',
  { line1: string; line2: string }
> = {
  en: {
    line1:
      "🧪 BitsLegacy is currently in Beta: we're testing and improving the platform to offer you maximum security.",
    line2:
      "🔐 You're welcome to use it freely, but we recommend not storing extremely sensitive or critical data until the final release.",
  },
  it: {
    line1:
      '🧪 BitsLegacy è attualmente in versione Beta: stiamo testando e migliorando la piattaforma per offrirti la massima sicurezza.',
    line2:
      '🔐 Puoi usarla liberamente, ma ti consigliamo di non inserire dati estremamente sensibili o critici fino al rilascio della versione definitiva.',
  },
}

export function HeroSection() {
  const { t, i18n } = useTranslation()
  const { address, isConnected, connect } = useWallet()
  const navigate = useNavigate()
  const pendingRoute = useRef<string | null>(null)
  const [acceptedTerms, setAcceptedTerms] = useState(() => true)
  const betaLang: 'en' | 'it' = (
    i18n.resolvedLanguage ?? i18n.language
  ).startsWith('it')
    ? 'it'
    : 'en'
  const betaFallback = HERO_BETA_FALLBACK[betaLang]
  const { record: recordConsent } = useTermsConsent()

  useEffect(() => {
    if (isConnected && pendingRoute.current) {
      const route = pendingRoute.current
      pendingRoute.current = null
      navigate(route)
    }
  }, [isConnected, navigate])

  const proceed = (source: TermsConsentSource, route: string) => {
    if (!acceptedTerms) return
    // Fire-and-forget: never block the user's primary action on the
    // consent log write. RLS + idempotent UI mean a transient failure is
    // recoverable on a later interaction.
    void recordConsent({
      source,
      walletAddress: address ?? null,
      language: i18n.language,
    })
    if (isConnected) {
      navigate(route)
    } else {
      pendingRoute.current = route
      connect()
    }
  }

  const handleBenefactor = () => proceed('hero_benefactor', '/benefactor')
  const handleBeneficiary = () => proceed('hero_beneficiary', '/dashboard')

  const ctaDisabledClass = !acceptedTerms ? 'cursor-not-allowed opacity-50' : ''

  return (
    <section className="relative flex min-h-[calc(80vh-var(--header-height))] items-center justify-center overflow-hidden text-center max-md:min-h-[calc(70vh-var(--header-height))]">
      <div
        className="pointer-events-none absolute top-[-30%] left-1/2 z-0 h-[min(700px,100vw)] w-[min(700px,100vw)] -translate-x-1/2 rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgba(201, 168, 76, 0.08) 0%, transparent 70%)',
        }}
      />
      <div className="relative z-10 max-w-[720px] px-4 py-16 max-md:px-4 max-md:py-12 md:px-8 md:py-24">
        <h1 className="mb-6 bg-[linear-gradient(135deg,#f1f5f9_30%,var(--color-brand)_100%)] bg-clip-text text-[clamp(2rem,5vw,3.5rem)] leading-tight font-extrabold tracking-tight text-transparent">
          {t('hero.title')}
        </h1>
        <p className="text-fg-secondary mx-auto mb-6 max-w-[560px] text-[clamp(0.9375rem,2vw,1.25rem)] leading-relaxed">
          {t('hero.subtitle')}
        </p>
        <div
          data-testid="hero-beta-notice"
          className="border-line bg-surface-muted/35 mx-auto mb-6 max-w-[560px] space-y-3 rounded-xl border px-5 py-4 text-left shadow-md backdrop-blur-sm"
        >
          <p className="text-fg-secondary text-[clamp(0.875rem,1.8vw,1.0625rem)] leading-relaxed">
            {t('hero.betaLine1', { defaultValue: betaFallback.line1 })}
          </p>
          <p className="text-fg-secondary text-[clamp(0.875rem,1.8vw,1.0625rem)] leading-relaxed">
            {t('hero.betaLine2', { defaultValue: betaFallback.line2 })}
          </p>
        </div>
        <TermsCheckbox
          id="hero-terms"
          checked={acceptedTerms}
          onChange={setAcceptedTerms}
          showRequiredHint
          className="mb-8"
        />
        <div
          data-testid="hero-cta-group"
          className="flex flex-wrap justify-center gap-4 max-md:flex-col max-md:items-center"
        >
          <button
            type="button"
            disabled={!acceptedTerms}
            aria-disabled={!acceptedTerms}
            title={!acceptedTerms ? t('terms.requiredHint') : undefined}
            className={cn(
              'bg-gradient-brand text-fg-inverse shadow-glow duration-base hover:shadow-glow-lg rounded-lg border-none px-8 py-3.5 text-base font-semibold transition-all hover:-translate-y-0.5 max-md:w-full max-md:max-w-[320px]',
              ctaDisabledClass,
            )}
            onClick={handleBenefactor}
          >
            {t('hero.becomeBenefactor')}
          </button>
          <button
            type="button"
            disabled={!acceptedTerms}
            aria-disabled={!acceptedTerms}
            title={!acceptedTerms ? t('terms.requiredHint') : undefined}
            className={cn(
              'border-line text-fg duration-base hover:border-accent hover:text-accent rounded-lg border bg-transparent px-8 py-3.5 text-base font-semibold transition-all hover:-translate-y-0.5 max-md:w-full max-md:max-w-[320px]',
              ctaDisabledClass,
            )}
            onClick={handleBeneficiary}
          >
            {t('hero.imBeneficiary')}
          </button>
        </div>
      </div>
    </section>
  )
}
